import React, { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled, { css } from 'styled-components';

import { IconArrowLeft, IconCalendarClock, IconCheck, IconMapPin, IconTruckDelivery, IconX } from '@tabler/icons-react';

import { Loading } from 'components/Loading';
import { IconButton } from 'ui/Button';

import {
    DeliveryState,
    DeliveryTrackingStepKind,
    GetDeliveryTrackingQuery,
    useGetDeliveryTrackingQuery,
} from 'data/__generated__';

import { formatHour, formatHourRange, formatLongDateFR, initialsOf } from './formatHour';

type Tracking = GetDeliveryTrackingQuery['deliveryTracking'];

const STEP_LABELS: Record<DeliveryTrackingStepKind, string> = {
    [DeliveryTrackingStepKind.Loaded]: 'Chargée',
    [DeliveryTrackingStepKind.OnTheRoad]: 'En route',
    [DeliveryTrackingStepKind.OnSite]: 'Arrivé sur place',
    [DeliveryTrackingStepKind.Delivered]: 'Livrée',
    [DeliveryTrackingStepKind.Failed]: 'Non livrée',
};

/** Tant que l'arrêt n'est pas clos, la page se rafraîchit toute seule : la tournée avance sans nous. */
const ONGOING_POLL_INTERVAL_MS = 30_000;

const CLOSED_STATES = [
    DeliveryState.Delivered,
    DeliveryState.Archived,
    DeliveryState.NotDelivered,
    DeliveryState.Aborted,
];

export const DeliveryTracking: React.FC = () => {
    const { deliveryId } = useParams();
    const navigate = useNavigate();

    const { data, loading, error, stopPolling } = useGetDeliveryTrackingQuery({
        variables: { deliveryId: deliveryId ?? '' },
        skip: !deliveryId,
        pollInterval: ONGOING_POLL_INTERVAL_MS,
    });

    const tracking = data?.deliveryTracking;
    const isClosed = tracking ? CLOSED_STATES.includes(tracking.state) : false;

    // Le transporteur garde la main tant qu'il fournit un lien : sa page en sait plus que la nôtre.
    // On couvre ici les entrées directes (favori, notification) ; le lien de la commande, lui, pointe
    // déjà droit sur Urbantz, sans rebond.
    const carrierTrackingUrl = tracking?.trackingUrl && !window.__TOTENV__?.TRACKING_PAGE ? tracking.trackingUrl : null;

    useEffect(() => {
        if (carrierTrackingUrl) {
            window.location.replace(carrierTrackingUrl);
        }
    }, [carrierTrackingUrl]);

    // Une fois l'arrêt clos, plus rien ne bougera : inutile de continuer à interroger l'API.
    useEffect(() => {
        if (isClosed) {
            stopPolling();
        }
    }, [isClosed, stopPolling]);

    if ((loading && !tracking) || carrierTrackingUrl) {
        return <Loading />;
    }

    return (
        <Container>
            <PageHeader>
                <IconButton data-test="tracking-back" $rotateOnHover $large $white onClick={() => navigate(-1)}>
                    <IconArrowLeft size={25} />
                </IconButton>
            </PageHeader>
            <ScrollableSection>
                <PageTitleSection>
                    <IconTruckDelivery size={21} />
                    <PageTitle>Suivi de la livraison</PageTitle>
                </PageTitleSection>

                {error || !tracking ? (
                    <EmptyState>Le suivi de cette livraison n'est pas disponible.</EmptyState>
                ) : (
                    <Content>
                        <StatusCard tracking={tracking} isClosed={isClosed} />

                        <CardRow>
                            <PlannedCard tracking={tracking} />
                            <DeliveryPersonCard name={tracking.deliveryPersonName} />
                        </CardRow>

                        <RecipientCard recipient={tracking.recipient} />

                        {tracking.photos.length ? (
                            <Card>
                                <CardHeader>
                                    <CardTitle>Photos de la livraison</CardTitle>
                                </CardHeader>
                                <Photos>
                                    {tracking.photos.map(({ _id, url }) => (
                                        <Photo key={_id} src={url} alt="Photo de livraison" />
                                    ))}
                                </Photos>
                            </Card>
                        ) : null}
                    </Content>
                )}
            </ScrollableSection>
        </Container>
    );
};

/** L'en-tête du suivi : à qui on parle, où on en est, et la frise. Même carte que les trois autres. */
const StatusCard: React.FC<{ tracking: Tracking; isClosed: boolean }> = ({ tracking, isClosed }) => {
    const { recipient, steps, stopsBefore } = tracking;

    // Un jalon plus tardif atteint prouve les précédents : un arrêt en route est forcément chargé, même
    // si le scan de chargement manque. La date, elle, n'est affichée que si l'étape en porte une.
    const lastReachedIndex = steps.reduce((last, { reachedAt }, index) => (reachedAt ? index : last), -1);

    return (
        <StatusBlock data-test="tracking-summary">
            {recipient.contactName ? <Greeting>Bonjour {recipient.contactName}</Greeting> : null}
            <Headline>{getHeadline(tracking)}</Headline>

            {!isClosed && stopsBefore !== null && stopsBefore !== undefined ? (
                <SubLine data-test="tracking-stops-before">
                    {stopsBefore === 0
                        ? 'Votre arrêt est le prochain.'
                        : `${stopsBefore} arrêt${stopsBefore > 1 ? 's' : ''} avant le vôtre.`}
                </SubLine>
            ) : null}

            <Steps data-test="tracking-timeline">
                {steps.map(({ kind, reachedAt }, index) => {
                    const reached = index <= lastReachedIndex;

                    return (
                        <Step key={kind} data-reached={reached}>
                            <StepTrack>
                                <Segment $filled={reached} $hidden={index === 0} />
                                <StepDot $reached={reached} $failed={kind === DeliveryTrackingStepKind.Failed}>
                                    {reached ? (
                                        kind === DeliveryTrackingStepKind.Failed ? (
                                            <IconX size={14} />
                                        ) : (
                                            <IconCheck size={14} />
                                        )
                                    ) : null}
                                </StepDot>
                                <Segment $filled={index < lastReachedIndex} $hidden={index === steps.length - 1} />
                            </StepTrack>
                            <StepLabel $reached={reached}>{STEP_LABELS[kind]}</StepLabel>
                            <StepHour>{reachedAt ? formatHour(reachedAt) : ''}</StepHour>
                        </Step>
                    );
                })}
            </Steps>
        </StatusBlock>
    );
};

const PlannedCard: React.FC<{ tracking: Tracking }> = ({ tracking }) => {
    const { deliveryDate, plannedSlot, estimatedSlot } = tracking;
    const slot = estimatedSlot ?? plannedSlot;
    const range = formatHourRange(slot.start, slot.end);

    return (
        <Card>
            <CardHeader>
                <IconCalendarClock size={18} />
                <CardTitle>{estimatedSlot ? 'Passage estimé' : 'Livraison prévue le'}</CardTitle>
            </CardHeader>
            <CardText>
                {formatLongDateFR(deliveryDate)}
                {range ? `, ${range.replace('entre ', '').replace(' et ', ' - ')}` : ''}
            </CardText>
            {estimatedSlot ? <CardHint>Estimation recalculée au fil de la tournée.</CardHint> : null}
        </Card>
    );
};

const DeliveryPersonCard: React.FC<{ name?: string | null }> = ({ name }) => (
    <Card>
        <CardHeader>
            <IconTruckDelivery size={18} />
            <CardTitle>Votre livreur</CardTitle>
        </CardHeader>
        {name ? (
            <DeliveryPerson>
                <Avatar>{initialsOf(name)}</Avatar>
                <CardText>{name}</CardText>
            </DeliveryPerson>
        ) : (
            <CardHint>Affiché au départ de la tournée.</CardHint>
        )}
    </Card>
);

const RecipientCard: React.FC<{ recipient: Tracking['recipient'] }> = ({ recipient }) => (
    <Card>
        <CardHeader>
            <IconMapPin size={18} />
            <CardTitle>Vos coordonnées</CardTitle>
        </CardHeader>
        <CardText $strong>{recipient.name}</CardText>
        {recipient.contactName ? <CardText>{recipient.contactName}</CardText> : null}
        {recipient.addressLines.map((line) => (
            <CardHint key={line}>{line}</CardHint>
        ))}
    </Card>
);

/**
 * Une seule phrase, celle qui répond à la question posée — la même que la page transporteur donnait.
 * L'estimation prime quand elle existe, sinon le créneau annoncé, qui n'est jamais faux.
 */
function getHeadline({ state, estimatedSlot, plannedSlot, deliveredAt, deliveryDate }: Tracking): string {
    // C'est l'API qui décide si l'arrêt est livré (`deliveredAt` n'est posé que dans ce cas) : le front
    // n'a pas à rejouer la règle état par état, et n'oublie donc pas `Archived`.
    if (deliveredAt) {
        return `Votre commande a été livrée le ${formatLongDateFR(deliveryDate)} à ${formatHour(deliveredAt)}`;
    }

    if (state === DeliveryState.Delivered || state === DeliveryState.Archived) {
        return 'Votre commande a été livrée';
    }

    if (state === DeliveryState.NotDelivered || state === DeliveryState.Aborted) {
        return "Votre commande n'a pas pu être livrée";
    }

    const estimated = formatHourRange(estimatedSlot?.start, estimatedSlot?.end);
    if (estimated) {
        return `Votre commande arrive ${estimated}`;
    }

    if (state === DeliveryState.InProgress) {
        return 'Votre commande est en route';
    }

    const planned = formatHourRange(plannedSlot.start, plannedSlot.end);

    return `Votre commande est prévue le ${formatLongDateFR(deliveryDate)}${planned ? `, ${planned}` : ''}`;
}

const Container = styled.div`
    display: flex;
    flex-direction: column;
    background-color: white;
    position: relative;
    flex-shrink: 1;
    width: 100%;
    height: 100%;
`;

const ScrollableSection = styled.div`
    overflow-y: auto;
    display: flex;
    flex-flow: column;
`;

const PageHeader = styled.header`
    display: flex;
    width: 100%;
    justify-content: space-between;
    padding: 1em 1.5em;
    background: ${({ theme }) => theme.colors.green};
`;

const PageTitleSection = styled.div`
    padding: 25px;
    display: flex;
    align-items: center;
    background: ${({ theme }) => theme.colors.culturedGrey};
    border-bottom: 1px solid ${({ theme }) => theme.colors.borderGrey};
`;

const PageTitle = styled.h2`
    margin: 0;
    padding: 0 0 0 10px;
`;

const Content = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 25px;
`;

const StatusBlock = styled.section`
    border: 1px solid ${({ theme }) => theme.colors.lightGrey};
    border-radius: 12px;
    padding: 20px;
`;

const Greeting = styled.p`
    margin: 0 0 2px;
    font-size: 14px;
    color: ${({ theme }) => theme.colors.pantinGrey};
`;

const Headline = styled.p`
    margin: 0;
    font-size: 20px;
    font-weight: 600;
`;

const SubLine = styled.p`
    margin: 4px 0 0;
    font-size: 14px;
    color: ${({ theme }) => theme.colors.darkGrey};
`;

const Steps = styled.ol`
    display: flex;
    align-items: flex-start;
    list-style: none;
    margin: 24px 0 0;
    padding: 0;
`;

const Step = styled.li`
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
`;

const StepTrack = styled.div`
    display: flex;
    align-items: center;
    width: 100%;
`;

const Segment = styled.span<{ $filled: boolean; $hidden: boolean }>`
    flex: 1;
    height: 1px;
    background: ${({ $filled, $hidden, theme }) =>
        $hidden ? 'transparent' : $filled ? theme.colors.black : theme.colors.lightGrey};
`;

const StepDot = styled.span<{ $reached: boolean; $failed: boolean }>`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    flex-shrink: 0;
    border-radius: 50%;
    border: 1px solid ${({ theme }) => theme.colors.lightGrey};
    background: white;
    color: white;

    ${({ $reached, $failed, theme }) =>
        $reached &&
        css`
            background: ${$failed ? theme.colors.lightRed : theme.colors.black};
            border-color: ${$failed ? theme.colors.lightRed : theme.colors.black};
        `}
`;

const StepLabel = styled.span<{ $reached: boolean }>`
    margin-top: 8px;
    font-size: 13px;
    color: ${({ $reached, theme }) => ($reached ? theme.colors.black : theme.colors.pantinGrey)};
`;

const StepHour = styled.span`
    font-size: 12px;
    color: ${({ theme }) => theme.colors.pantinGrey};
    font-variant-numeric: tabular-nums;
`;

const CardRow = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;

    @media (max-width: 800px) {
        grid-template-columns: 1fr;
    }
`;

const Card = styled.section`
    border: 1px solid ${({ theme }) => theme.colors.lightGrey};
    border-radius: 12px;
    padding: 20px;
`;

const CardHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 10px;
`;

const CardTitle = styled.h3`
    margin: 0;
    font-size: 16px;
`;

const CardText = styled.p<{ $strong?: boolean }>`
    margin: 0;
    font-weight: ${({ $strong }) => ($strong ? 600 : 400)};
`;

const CardHint = styled.p`
    margin: 2px 0 0;
    font-size: 13px;
    color: ${({ theme }) => theme.colors.pantinGrey};
`;

const DeliveryPerson = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
`;

const Avatar = styled.span`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: ${({ theme }) => theme.colors.black};
    color: white;
    font-size: 14px;
`;

const Photos = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
`;

const Photo = styled.img`
    width: 90px;
    height: 90px;
    object-fit: cover;
    border-radius: 6px;
`;

const EmptyState = styled.div`
    padding: 40px 25px;
    color: ${({ theme }) => theme.colors.darkGrey};
`;
